import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get, head, isEmpty } from 'lodash-es';
import { Table } from 'reactstrap';
import { Element } from 'react-scroll'; // eslint-disable-line no-restricted-syntax

// Models
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { CostToOwnModel, TcoEntities, buildTcoPath, hasTcoData } from 'client/data/models/cost-to-own';
import { VisitorModel } from 'client/data/models/visitor';

// Entities
import { AppraisalEntities } from 'client/data/models/appraisal';

// Utils
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { formatPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { getDeprecationYears } from 'client/site-modules/shared/utils/appraiser/deprecation-infographic';

// Shared Components
import { AppraiseNowForm } from 'site-modules/shared/components/appraise-now-form/appraise-now-form';
import { isEstimatedTmvConditions } from 'site-modules/shared/components/estimated-values/estimated-values-utils';
import { PoweredByEdmunds } from 'site-modules/shared/components/powered-by-edmunds/powered-by-edmunds';
import { DeprecationInfographic } from 'client/site-modules/shared/components/deprecation-infographic/deprecation-infographic';
import { RenderWhenViewable } from 'site-modules/shared/components/render-when-viewable/render-when-viewable';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';

import './deprecation-graphic.scss';

const DEPRECATION_GRAPHIC_CREATIVE_ID = 'edm-entry-depreciation';

export class DepreciationGraphicUI extends Component {
  static propTypes = {
    vehicle: VehicleEntities.StyleWithoutSubmodels,
    onContinue: PropTypes.func,
    style: AppraisalEntities.Style,
    styleId: PropTypes.number,
    tcoStyle: TcoEntities.Style,
    isMobile: PropTypes.bool,
    zipCode: PropTypes.string,
  };

  static defaultProps = {
    vehicle: {},
    onContinue: null,
    style: {},
    styleId: undefined,
    tcoStyle: {},
    isMobile: false,
    zipCode: '',
    stateCode: '',
  };

  shouldComponentUpdate(nextProps) {
    const { styleId, zipCode } = this.props;

    return styleId !== nextProps.styleId || zipCode !== nextProps.zipCode;
  }

  onContinue = vehicleOverride => {
    this.props.onContinue(vehicleOverride, DEPRECATION_GRAPHIC_CREATIVE_ID);
  };

  renderHelpfulInformationBlock = ({ icon, title, list }) => (
    <div key={title}>
      <div className="d-flex align-items-center">
        <i
          className={classNames(
            icon,
            'helpful-information-icon text-primary-darker rounded-circle d-flex align-items-center justify-content-center bg-white'
          )}
          style={{ width: '40px', height: '40px', borderWidth: '1px', borderStyle: 'solid' }}
        />
        <div className="ms-0_75 fw-bold">{title}</div>
      </div>
      <ul className="list-unstyled ms-3_5">
        {list.map(itemTitle => (
          <li key={itemTitle}>
            <span aria-hidden className="d-inline-block text-gray-darker me-0_75">
              -
            </span>
            {itemTitle}
          </li>
        ))}
      </ul>
    </div>
  );

  render() {
    const { vehicle, style, tcoStyle, isMobile } = this.props;
    const makeName = get(vehicle, 'makeName', '');
    const modelName = get(vehicle, 'modelName', '');
    const year = get(vehicle, 'year', '');
    const styleName = get(style, 'styleName', '');
    const totalDepreciation = get(tcoStyle, 'total.depreciation', 0);
    const depreciationYears = getDeprecationYears(tcoStyle, year);
    const tmvTradeIn = get(style, 'tmv.conditions.CLEAN.usedTradeIn', 0);
    const tmvPrivateParty = get(style, 'tmv.conditions.CLEAN.usedPrivateParty', 0);
    const HELPFUL_INFO_BLOCKS = [
      {
        icon: 'icon-price-tag',
        title: 'Estimated Value Today',
        list: [
          <span className="fw-bold text-primary-darker">
            {formatPriceStringV2(tmvTradeIn)} - {formatPriceStringV2(tmvPrivateParty)}
          </span>,
          'Based on good condition.',
        ],
      },
      {
        icon: 'icon-stats-decline',
        title: 'Depreciation Insights',
        list: [
          'Different cars depreciate at different rates.',
          <Fragment>
            In general, new cars lose <b className="text-primary-darker">60%</b> of their MSRP in the first five years.
          </Fragment>,
        ],
      },
      {
        icon: 'icon-appraisal',
        title: 'Know Your Car’s Worth',
        list: [
          'Nearly every vehicle depreciates over time.',
          'With our free appraisal tool, you can get an accurate price for your car in minutes.',
        ],
      },
    ];
    const vehicleYmm = `${year} ${makeName} ${modelName}`;

    return (
      <div
        data-tracking-parent={DEPRECATION_GRAPHIC_CREATIVE_ID}
        className={classNames('depreciation-graphic rounded py-1_5 px-0 px-md-3_5 my-1_5 bg-gray-extra-light')}
        style={{ borderWidth: '1px', borderStyle: 'solid' }}
      >
        <div className="depreciation-graphic-container px-md-0_5">
          <div className="size-24 fw-bold mb-0_5 px-1 px-md-0 text-cool-gray-10">
            Know the value of your {vehicleYmm} over time
          </div>
          <div className="mb-1_5 text-gray-darker px-1 px-md-0">
            Did you know your car depreciates every year? Here is a guide to help you understand the value of your car.
          </div>
          <DeprecationInfographic
            vehicle={vehicle}
            style={style}
            tcoStyle={tcoStyle}
            isMobile={isMobile}
            className="mb-1_5"
          />
          <div className="depreciation-table mb-1_5 bg-white rounded pb-1_5">
            <div className="table-header bg-primary-opacity-2 pt-1_5 px-1 px-md-3_5 pb-1 mb-1">
              <div className="size-24 fw-bold text-gray-darker mb-0_5">Value breakdown</div>
              <div className="text-gray-darker size-14 mb-0_25">{vehicleYmm}</div>
              <div className="text-gray-30 size-12">{styleName} with no options</div>
            </div>
            <div className="px-1 px-md-3_5">
              <Table responsive className="text-gray-darker">
                <thead>
                  <tr className="size-14 fw-medium">
                    <th className="border-top-0 ps-0 pt-0">Year</th>
                    <th className="text-end border-top-0 pt-0">Car Value</th>
                    <th className="text-end border-top-0 pe-0 pt-0">Depreciation</th>
                  </tr>
                </thead>
                <tbody>
                  {depreciationYears.map(yearPrice => (
                    <tr className={classNames({ 'fw-bold': yearPrice.active })} key={`${yearPrice.year}-table`}>
                      <td className="ps-0">
                        {yearPrice.year} ({vehicleYmm})
                      </td>
                      <td align="right">{formatPriceStringV2(yearPrice.price)}</td>
                      <td align="right" className="pe-0">
                        -{formatPriceStringV2(yearPrice.depreciation)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div className="d-flex justify-content-between align-items-center px-1 px-md-3_5">
              <div>
                <div className="text-gray-darker">Total Depreciation</div>
                <div className="size-14 text-gray-dark">Over five years</div>
              </div>
              <div className="text-gray-darker size-20 fw-medium text-end">
                -{formatPriceStringV2(totalDepreciation)}
              </div>
            </div>
          </div>
          <div className="helpful-information text-gray-darker mb-2 px-1 px-md-0">
            <div className={classNames('size-20 mb-0_75 fw-medium')}>Helpful information about your {vehicleYmm}</div>
            {HELPFUL_INFO_BLOCKS.map(this.renderHelpfulInformationBlock)}
          </div>
          <div className="pb-0_5">
            <MinHeightWrapper componentType="DEPRECATION_GRAPHIC_VEHICLE_DROPDOWNS">
              <RenderWhenViewable verticalOffset="50%">
                <div className="start-appraisal px-1 px-md-0">
                  <div className="size-20 fw-medium text-gray-darker mb-0_75">Appraise Your {vehicleYmm} Now</div>
                  <div className="text-gray-darker mb-1_5">
                    Get an accurate appraisal and see how much your {vehicleYmm} is worth.
                  </div>
                  <AppraiseNowForm vehicle={vehicle} onContinue={this.onContinue} />
                </div>
              </RenderWhenViewable>
            </MinHeightWrapper>
          </div>
        </div>
        <PoweredByEdmunds className="mt-1" />
      </div>
    );
  }
}

// eslint-disable-next-line react/no-multi-comp
export class DepreciationGraphicWrapper extends Component {
  static propTypes = {
    appraisalStyles: AppraisalEntities.Styles,
    tcoStyles: TcoEntities.Style,
    vehicle: VehicleEntities.StyleWithoutSubmodels,
    onContinue: PropTypes.func,
    isMobile: PropTypes.bool,
    pathname: PropTypes.string,
    zipCode: PropTypes.string,
    stateCode: PropTypes.string,
  };

  static defaultProps = {
    appraisalStyles: [],
    tcoStyles: {},
    vehicle: {},
    onContinue: null,
    isMobile: false,
    pathname: '',
    zipCode: '',
    stateCode: '',
  };

  shouldComponentUpdate(nextProps) {
    const { appraisalStyles, tcoStyles } = this.props;

    return (
      (isEmpty(appraisalStyles) && !isEmpty(nextProps.appraisalStyles)) ||
      (isEmpty(tcoStyles) && !isEmpty(nextProps.tcoStyles))
    );
  }

  render() {
    const { appraisalStyles, tcoStyles, ...restProps } = this.props;
    const appraisalStylesFiltered = appraisalStyles.filter(appraisalStyle => isEstimatedTmvConditions(appraisalStyle));
    const style = head(appraisalStylesFiltered);
    const styleId = get(style, 'styleId', '');
    const tcoStyle = get(tcoStyles, styleId, {});

    return isEmpty(tcoStyle) ? null : (
      <Element id="depreciation">
        <DepreciationGraphicUI tcoStyle={tcoStyle} style={style} styleId={styleId} {...restProps} />
      </Element>
    );
  }
}

export const stateToPropsConfig = {
  tcoStyles: bindToPath(
    ({ vehicle }) =>
      hasTcoData(vehicle.year)
        ? buildTcoPath({ make: vehicle.makeSlug, model: vehicle.modelSlug, year: vehicle.year })
        : null,
    CostToOwnModel
  ),
  zipCode: bindToPath('location.zipCode', VisitorModel),
  stateCode: bindToPath('location.stateCode', VisitorModel),
};

export const DepreciationGraphic = connectToModel(DepreciationGraphicWrapper, stateToPropsConfig);
